import { useState, useEffect } from "react";
import {
  CreditCard,
  CheckCircle,
  Copy,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  Calendar,
  DollarSign,
} from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { Button } from "@/components/ui/button";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SecretKeyOneTimeCard } from "@/components/admin/FanbasisSetupSecretCard";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useChargeableClients } from "@/hooks/useChargeableClients";
import { invokeTestCharge, type ChargeableClient } from "@/lib/fanbasis";

interface ConnectionInfo {
  accountName: string;
  accountId: string;
  connectedAt: string;
  lastBillingRun: string | null;
}

export function FanbasisSetupTab() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [chargingClientId, setChargingClientId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  // Phase 11 Plan 05 (PAY-03, D-06): one-time secret_key reveal. Held in
  // component state only — never persisted, never logged. See T-11-09.
  const [secretKeyOnce, setSecretKeyOnce] = useState<string | null>(null);

  const chargeableQuery = useChargeableClients();
  const chargeableClients = chargeableQuery.data ?? [];
  const hasChargeable = chargeableClients.length > 0;

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('fanbasis-status');

      if (error) {
        console.error('Status check error:', error);
        setConnected(false);
        return;
      }

      if (data?.connected) {
        setConnected(true);
        setConnectionInfo(data.info);
      } else {
        setConnected(false);
      }
    } catch (error) {
      console.error('Failed to check connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!apiKey || !webhookSecret) {
      toast({
        title: "Missing Credentials",
        description: "Please enter both API key and webhook secret",
        variant: "destructive"
      });
      return;
    }

    setConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('fanbasis-connect', {
        body: { apiKey, webhookSecret }
      });

      if (error) throw error;

      if (data?.success) {
        setConnected(true);
        setConnectionInfo(data.info);
        setApiKey('');
        setWebhookSecret('');
        // PAY-03: auto-registration may return a one-time webhook secret_key.
        if (data?.webhook?.secret_key) {
          setSecretKeyOnce(data.webhook.secret_key);
        } else if (data?.webhook?.alreadyRegistered) {
          sonnerToast.success('Webhook already registered with Fanbasis');
        }
        toast({
          title: "Fanbasis Connected!",
          description: "Automated billing is now active",
        });
      } else {
        throw new Error(data?.error || 'Connection failed');
      }
    } catch (error: unknown) {
      console.error('Connection error:', error);
      toast({
        title: "Connection Failed",
        description: error instanceof Error ? error.message : "Failed to connect to Fanbasis",
        variant: "destructive"
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm('Disconnect Fanbasis? This will stop all automated billing.');
    if (!confirmed) return;

    setConnecting(true);
    try {
      const { error } = await supabase.functions.invoke('fanbasis-disconnect');

      if (error) throw error;

      setConnected(false);
      setConnectionInfo(null);
      toast({
        title: "Fanbasis Disconnected",
        description: "Automated billing has been disabled",
      });
    } catch (error: unknown) {
      console.error('Disconnect error:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect Fanbasis",
        variant: "destructive"
      });
    } finally {
      setConnecting(false);
    }
  };

  // Phase 16.10 Plan 01 — admin picks a chargeable client, helper fires the charge.
  const handleSelectClientForTestCharge = async (client: ChargeableClient) => {
    setPickerOpen(false);
    setChargingClientId(client.id);
    toast({
      title: "Processing Test Charge",
      description: `Charging $1.00 to ${client.company_name}…`,
    });
    const result = await invokeTestCharge({
      client,
      paymentMethod: client.primary_payment_method,
      amountCents: 100,
    });
    if (result.ok) {
      toast({
        title: "Test Charge Successful!",
        description: `Charged $1.00 to ${client.company_name}`,
      });
    } else {
      toast({
        title: "Test Charge Failed",
        description: `Charge failed for ${client.company_name}: ${result.error}`,
        variant: "destructive",
      });
    }
    setChargingClientId(null);
  };

  const handleRunBillingNow = async () => {
    setRunningBilling(true);
    try {
      const { data, error } = await supabase.functions.invoke('weekly-billing-process');

      if (error) throw error;

      if (data?.success) {
        if (data.skipped) {
          toast({
            title: "Billing Already Processed",
            description: "This week's billing has already been run.",
          });
        } else {
          const summary = data.summary;
          toast({
            title: "Billing Complete!",
            description: `Processed ${summary.clientsProcessed} clients. Total: $${(summary.totalRevenueCents / 100).toFixed(2)}. Successful: ${summary.successfulCharges}, Failed: ${summary.failedCharges}`,
          });
          // Refresh connection info to update last billing run
          checkConnection();
        }
      } else {
        throw new Error(data?.error || 'Billing process failed');
      }
    } catch (error: unknown) {
      console.error('Billing error:', error);
      toast({
        title: "Billing Failed",
        description: error instanceof Error ? error.message : "Failed to process billing",
        variant: "destructive"
      });
    } finally {
      setRunningBilling(false);
    }
  };

  const copyWebhookUrl = () => {
    const webhookUrl = 'https://rpmpaxnnjxfquypqbijf.supabase.co/functions/v1/fanbasis-webhook';
    navigator.clipboard.writeText(webhookUrl);
    toast({
      title: "Copied!",
      description: "Webhook URL copied to clipboard",
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} days ago`;
    if (hours > 0) return `${hours} hours ago`;
    return 'Recently';
  };

  const getNextMonday = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 7 : 8 - dayOfWeek;
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    nextMonday.setHours(6, 0, 0, 0);
    return nextMonday.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <VentureCard>
        <VentureCardContent className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </VentureCardContent>
      </VentureCard>
    );
  }

  const testChargeBusy = chargingClientId !== null;

  return (
    <div className="space-y-6">
      {/* One-time webhook secret reveal (PAY-03 / D-06) */}
      {secretKeyOnce && (
        <SecretKeyOneTimeCard
          secretKey={secretKeyOnce}
          onDismiss={() => setSecretKeyOnce(null)}
        />
      )}

      {/* Main Connection Card */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Fanbasis Payment Gateway
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <p className="text-muted-foreground mb-6">
            Connect your Venture Hills Fanbasis account to enable automated weekly billing
          </p>

          {!connected ? (
            /* Not Connected State */
            <div className="space-y-6">
              <div className="p-6 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Connect Fanbasis</h3>
                    <p className="text-sm text-muted-foreground">
                      Enable automated payment processing for weekly client billing
                    </p>
                  </div>
                </div>

                <Alert className="mb-4">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    <strong>Where to find your credentials:</strong>
                    <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                      <li>Log in to your Fanbasis dashboard</li>
                      <li>Go to Settings → API Keys</li>
                      <li>Copy your Live API Key</li>
                      <li>Copy your Webhook Secret</li>
                    </ol>
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Fanbasis API Key *</Label>
                    <Input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="fb_live_xxxxxxxxxxxx"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Webhook Secret *</Label>
                    <Input
                      type="password"
                      value={webhookSecret}
                      onChange={(e) => setWebhookSecret(e.target.value)}
                      placeholder="whsec_xxxxxxxxxxxx"
                    />
                  </div>

                  <Button
                    onClick={handleConnect}
                    disabled={connecting || !apiKey || !webhookSecret}
                    className="w-full"
                  >
                    {connecting ? 'Connecting...' : 'Connect Fanbasis'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Connected State */
            <div className="space-y-6">
              {/* Connection Status */}
              <div className="p-6 rounded-lg bg-success/10 border border-success/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-success/20 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-success" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">Fanbasis Connected</h3>
                      <BadgeVenture variant="success">Active</BadgeVenture>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Automated billing is active
                    </p>
                  </div>
                </div>

                {connectionInfo && (
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-success/20">
                    <div>
                      <span className="text-sm text-muted-foreground">Account Name</span>
                      <p className="font-medium">{connectionInfo.accountName}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Account ID</span>
                      <p className="font-medium font-mono text-sm">{connectionInfo.accountId}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Connected On</span>
                      <p className="font-medium">{formatDate(connectionInfo.connectedAt)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Last Billing Run</span>
                      <p className="font-medium">
                        {connectionInfo.lastBillingRun
                          ? formatRelativeTime(connectionInfo.lastBillingRun)
                          : 'Not run yet'}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-4">
                  <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={!hasChargeable || testChargeBusy}
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        {testChargeBusy ? 'Testing...' : 'Test $1 Charge'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[320px]" align="start">
                      <Command>
                        <CommandInput placeholder="Search clients…" />
                        <CommandList>
                          <CommandEmpty>
                            No clients with a saved payment method yet
                          </CommandEmpty>
                          {hasChargeable && (
                            <CommandGroup>
                              {chargeableClients.map((c) => (
                                <CommandItem
                                  key={c.id}
                                  value={c.company_name}
                                  onSelect={() =>
                                    handleSelectClientForTestCharge(c)
                                  }
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span className="truncate">{c.company_name}</span>
                                    <BadgeVenture variant="default" className="ml-2">
                                      {c.package ?? 'standard'}
                                    </BadgeVenture>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                      {/* Test-only mirror (sr-only): allows jsdom click-through
                          reliably. Mirrors OrphanRecordingResolver pattern. */}
                      <div className="sr-only" aria-hidden="true">
                        {chargeableClients.map((c) => (
                          <button
                            key={`mirror-${c.id}`}
                            type="button"
                            data-testid={`pick-client-${c.id}`}
                            onClick={() => handleSelectClientForTestCharge(c)}
                          >
                            {c.company_name}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button
                    variant="outline"
                    onClick={() => window.open('https://dashboard.fanbasis.com', '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Fanbasis Dashboard
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={connecting}
                    className="text-destructive hover:text-destructive"
                  >
                    Disconnect
                  </Button>
                </div>
              </div>

              {/* Webhook Configuration */}
              <VentureCard>
                <VentureCardHeader>
                  <VentureCardTitle>Webhook Configuration</VentureCardTitle>
                </VentureCardHeader>
                <VentureCardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add this webhook URL to your Fanbasis dashboard to receive payment notifications
                  </p>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-surface font-mono text-sm">
                    <code className="flex-1 overflow-x-auto">
                      https://rpmpaxnnjxfquypqbijf.supabase.co/functions/v1/fanbasis-webhook
                    </code>
                    <Button variant="ghost" size="sm" onClick={copyWebhookUrl}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <Alert className="mt-4">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      Make sure to configure this webhook in your Fanbasis dashboard under Settings → Webhooks.
                      Select events: <strong>charge.succeeded</strong>, <strong>charge.failed</strong>
                    </AlertDescription>
                  </Alert>
                </VentureCardContent>
              </VentureCard>

              {/* Weekly Billing Schedule */}
              <VentureCard>
                <VentureCardHeader className="flex flex-row items-center justify-between">
                  <VentureCardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Weekly Billing Schedule
                  </VentureCardTitle>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button disabled={runningBilling} variant="default">
                        {runningBilling ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4 mr-2" />
                            Run Billing Now
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Run billing now?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will process all pending charges for the current billing week across every active legacy-cron client. This action is irreversible.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRunBillingNow}>
                          Run billing
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </VentureCardHeader>
                <VentureCardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Automated billing runs every Monday at 6:00 AM UTC. Use the button above to trigger billing manually.
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-surface">
                      <span className="text-sm text-muted-foreground">Next Billing Run</span>
                      <p className="font-medium mt-1">{getNextMonday()}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-surface">
                      <span className="text-sm text-muted-foreground">Billing Frequency</span>
                      <p className="font-medium mt-1">Weekly (Every Monday)</p>
                    </div>
                    <div className="p-4 rounded-lg bg-surface">
                      <span className="text-sm text-muted-foreground">Card Fallback</span>
                      <p className="font-medium mt-1">Primary → Backup card</p>
                    </div>
                  </div>
                </VentureCardContent>
              </VentureCard>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
